import { initializeApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import { getFirestore } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  EmailAuthProvider,
  getAuth,
  linkWithCredential,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth'

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
}

export const isFirebaseConfigured = Object.values(firebaseConfig).every(Boolean)

export const app = isFirebaseConfigured ? initializeApp(firebaseConfig) : null

// App Check prueba que las llamadas vienen de esta app y no de un script.
// Se activa al configurar REACT_APP_RECAPTCHA_SITE_KEY; despues se puede
// forzar en las functions (ENFORCE_APP_CHECK) y en Firestore desde la consola.
const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY
if (app && recaptchaSiteKey) {
  // En localhost reCAPTCHA no valida: el SDK imprime en la consola un debug
  // token que se registra en Firebase > App Check > Administrar tokens de
  // depuracion. Nunca se activa en el build de produccion.
  if (process.env.NODE_ENV === 'development') {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = true
  }

  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  })
}

export const db = app ? getFirestore(app) : null

export const auth = app ? getAuth(app) : null

const functions = app ? getFunctions(app) : null

// Confirma contra la API de PayPal (via Cloud Function) que un subscriptionId
// aprobado por el boton de PayPal es real y esta ACTIVE antes de que la app
// desbloquee calendario/seguimiento. Sin esto, cualquiera podria simular la
// aprobacion desde el cliente y activar la membresia gratis.
export async function verifyPaypalSubscription(subscriptionId) {
  if (!functions) {
    throw new Error('firebase-unavailable')
  }

  const callVerify = httpsCallable(functions, 'verifyPaypalSubscription')
  const result = await callVerify({ subscriptionId })
  return result.data
}

// Cancela el cobro en PayPal y corta el acceso del lado del servidor.
export async function cancelPaypalSubscription() {
  if (!functions) {
    throw new Error('firebase-unavailable')
  }

  const callCancel = httpsCallable(functions, 'cancelPaypalSubscription')
  const result = await callCancel()
  return result.data
}

// El plan es contenido pago: lo genera el servidor solo si la membresia
// esta activa. startDate es la fecha local de hoy (YYYY-MM-DD).
export async function requestTrainingPlan({ level, daysPerWeek, startDate }) {
  if (!functions) {
    throw new Error('firebase-unavailable')
  }

  const callGenerate = httpsCallable(functions, 'generateTrainingPlan')
  const result = await callGenerate({ level, daysPerWeek, startDate })
  return result.data.plan
}

// El uid identifica el documento del atleta en Firestore (ver firestore.rules:
// solo el dueno del uid puede escribir su documento). Si ya hay una sesion
// (anonima o con email/password) auth.currentUser la refleja de forma
// sincronica, asi que la devolvemos directo. Si no hay nadie logueado,
// arrancamos un sign-in anonimo; el promise en curso se comparte entre
// llamadas concurrentes pero NO se cachea despues de resolver, para que un
// signOut() posterior dispare un sign-in nuevo en vez de reusar un uid viejo.
let anonymousSignInPromise = null

export function ensureAuthUser() {
  if (!auth) {
    return Promise.resolve(null)
  }

  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser.uid)
  }

  if (!anonymousSignInPromise) {
    anonymousSignInPromise = signInAnonymously(auth)
      .then(({ user }) => user.uid)
      .catch(() => null)
      .finally(() => {
        anonymousSignInPromise = null
      })
  }

  return anonymousSignInPromise
}

// Convierte el usuario anonimo en uno con email/password sin cambiar de uid,
// para que el perfil/plan ya guardados en Firestore queden atados a esa cuenta
// y se puedan recuperar despues de borrar la app o cambiar de dispositivo.
export async function linkEmailToCurrentUser(email, password) {
  await ensureAuthUser()
  if (!auth?.currentUser) {
    throw new Error('auth-unavailable')
  }

  const credential = EmailAuthProvider.credential(email, password)
  const { user } = await linkWithCredential(auth.currentUser, credential)
  return user
}

export async function loginWithEmail(email, password) {
  if (!auth) {
    throw new Error('auth-unavailable')
  }

  const { user } = await signInWithEmailAndPassword(auth, email, password)
  return user
}

export async function resetPassword(email) {
  if (!auth) {
    throw new Error('auth-unavailable')
  }

  await sendPasswordResetEmail(auth, email)
}

export async function signOutUser() {
  if (!auth) {
    return
  }

  await signOut(auth)
}

export function subscribeToAuthUser(callback) {
  if (!auth) {
    return () => {}
  }

  return onAuthStateChanged(auth, (user) => callback(user))
}
